import React, { useEffect, useState } from "react";
import { FaImage } from "react-icons/fa";

import {
  CheckboxCard,
  CheckboxLabel,
  FieldHeader,
  FieldHeaderAction,
  FieldHeaderCopy,
  FieldGroup,
  FieldHelper,
  FieldLabel,
  ImageControlStack,
  ImageFieldRow,
  ImagePlaceholder,
  ImagePlaceholderLabel,
  ImageSurface,
  HiddenFileInput,
  MetadataCollectionGrid,
  PreviewImage,
  SelectedFileSurface,
  SelectInput,
  TextInput,
  TextareaInput,
  UploadButton,
} from "./styles/admin-forms.styles";
import { MetadataRow, UploadableFile, UploadableImage } from "./types";
import { FlexibleCollectionField } from "./flexible-collection-field";
import { resolveImagePreview } from "./utils";

type ImagePickerFieldProps = {
  label: string;
  helper?: string;
  value?: UploadableImage;
  onChange: (value: UploadableImage) => void;
  placeholder?: string;
};

export const ImagePickerField: React.FC<ImagePickerFieldProps> = ({
  helper,
  label,
  onChange,
  placeholder = "https://example.com/image.jpg",
  value,
}) => {
  const imageFieldId = React.useId();
  const fileInputId = React.useId();
  const [previewUrl, setPreviewUrl] = useState(resolveImagePreview(value));

  useEffect(() => {
    if (typeof File !== "undefined" && value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreviewUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }

    setPreviewUrl(resolveImagePreview(value));
    return undefined;
  }, [value]);

  return (
    <FieldGroup>
      <FieldHeader>
        <FieldHeaderCopy>
          <FieldLabel htmlFor={imageFieldId}>{label}</FieldLabel>
          {helper ? <FieldHelper>{helper}</FieldHelper> : null}
        </FieldHeaderCopy>
        <FieldHeaderAction>
          <UploadButton htmlFor={fileInputId}>
            Choose file
            <HiddenFileInput
              accept="image/*"
              id={fileInputId}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  onChange(file);
                }
              }}
              type="file"
            />
          </UploadButton>
        </FieldHeaderAction>
      </FieldHeader>

      <ImageSurface>
        <ImageFieldRow>
          {previewUrl ? (
            <PreviewImage alt={label} fallbackLabel={label} src={previewUrl} />
          ) : (
            <ImagePlaceholder>
              <ImagePlaceholderLabel>
                <FaImage size={13} />
                No image selected yet
              </ImagePlaceholderLabel>
            </ImagePlaceholder>
          )}

          <ImageControlStack>
            <TextInput
              aria-label={label}
              id={imageFieldId}
              onChange={(event) => onChange(event.target.value)}
              placeholder={placeholder}
              value={typeof value === "string" ? value : ""}
            />
          </ImageControlStack>
        </ImageFieldRow>
      </ImageSurface>
    </FieldGroup>
  );
};

type FilePickerFieldProps = {
  label: string;
  helper?: string;
  value?: UploadableFile;
  onChange: (value: UploadableFile) => void;
  placeholder?: string;
  accept?: string;
};

export const FilePickerField: React.FC<FilePickerFieldProps> = ({
  accept,
  helper,
  label,
  onChange,
  placeholder = "https://example.com/file.pdf",
  value,
}) => {
  const fileFieldId = React.useId();
  const fileInputId = React.useId();
  const selectedLabel =
    typeof File !== "undefined" && value instanceof File
      ? value.name
      : typeof value === "string" && value
      ? value
      : "No file selected yet";

  return (
    <FieldGroup>
      <FieldHeader>
        <FieldHeaderCopy>
          <FieldLabel htmlFor={fileFieldId}>{label}</FieldLabel>
          {helper ? <FieldHelper>{helper}</FieldHelper> : null}
        </FieldHeaderCopy>
        <FieldHeaderAction>
          <UploadButton htmlFor={fileInputId}>
            Choose file
            <HiddenFileInput
              accept={accept}
              id={fileInputId}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  onChange(file);
                }
              }}
              type="file"
            />
          </UploadButton>
        </FieldHeaderAction>
      </FieldHeader>

      <ImageSurface>
        <SelectedFileSurface>{selectedLabel}</SelectedFileSurface>

        <TextInput
          aria-label={label}
          id={fileFieldId}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          value={typeof value === "string" ? value : ""}
        />
      </ImageSurface>
    </FieldGroup>
  );
};

type DynamicTextRowsFieldProps = {
  label: string;
  helper?: string;
  rows: string[];
  addLabel: string;
  placeholder: string;
  onChange: (rows: string[]) => void;
};

export const DynamicTextRowsField: React.FC<DynamicTextRowsFieldProps> = ({
  addLabel,
  helper,
  label,
  onChange,
  placeholder,
  rows,
}) => (
  <FlexibleCollectionField
    addLabel={addLabel}
    helper={helper}
    items={rows}
    label={label}
    onAdd={() => onChange([...rows, ""])}
    onRemove={(index) => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
    renderRow={(row, index) => (
      <TextInput
        onChange={(event) => {
          const nextRows = [...rows];
          nextRows[index] = event.target.value;
          onChange(nextRows);
        }}
        placeholder={placeholder}
        value={row}
      />
    )}
  />
);

type MetadataRowsProps = {
  rows: MetadataRow[];
  onChange: (rows: MetadataRow[]) => void;
};

export const MetadataRows: React.FC<MetadataRowsProps> = ({ onChange, rows }) => (
  <FlexibleCollectionField
    addLabel="Add field"
    helper="Add flexible key-value fields like edition, drop, or jersey_number."
    items={rows}
    label="Extra metadata"
    onAdd={() => onChange([...rows, { key: "", value: "" }])}
    onRemove={(index) => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
    renderRow={(row, index) => (
      <MetadataCollectionGrid>
        <TextInput
          aria-label={`Extra metadata key ${index + 1}`}
          onChange={(event) => {
            const nextRows = [...rows];
            nextRows[index] = { ...nextRows[index], key: event.target.value };
            onChange(nextRows);
          }}
          placeholder="Key"
          value={row.key}
        />
        <TextInput
          aria-label={`Extra metadata value ${index + 1}`}
          onChange={(event) => {
            const nextRows = [...rows];
            nextRows[index] = { ...nextRows[index], value: event.target.value };
            onChange(nextRows);
          }}
          placeholder="Value"
          value={row.value}
        />
      </MetadataCollectionGrid>
    )}
    rowSurface
  />
);

export const MetadataToggle: React.FC<{
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}> = ({ checked, label, onChange }) => (
  <CheckboxCard>
    <input checked={checked} onChange={(event) => onChange(event.target.checked)} type="checkbox" />
    <CheckboxLabel>{label}</CheckboxLabel>
  </CheckboxCard>
);

export const renderSelectOptions = (options: Array<{ label: string; value: string | number }>) =>
  options.map((option) => (
    <option key={`${option.value}`} value={option.value}>
      {option.label}
    </option>
  ));

export { SelectInput, TextInput, TextareaInput };
